import React, { useState, useEffect, useContext } from "react";
import { useRouter } from "next/router";
import PageKit from "../../data/PageKit";
import SpeedMeetingKit from "../../data/SpeedMeetingKit";
import HTMLRenderer from "../../components/library/HTMLRenderer";
import InteractiveVideoPlayer from "../../components/players/InteractiveVideoPlayer";
import VideoPlayer from "../../components/players/VideoPlayer";
import MeetingPlayer from "../../components/players/MeetingPlayer";
import LayoutPrivate from "../../components/LayoutPrivate";
import { GlobalPageDataContext } from "../../contexts/GlobalPageDataContext";
import { UserContext } from "../../contexts/UserContext";
import PageHead from "../../components/PageHead";
import { ChatContext } from "../../contexts/ChatContext";
import Custom404 from "../../components/Custom404";

import EventMobileInitView from "../../components/Views/EventMobileInitView";
import EventBrowserInitView from "../../components/Views/EventBrowserInitView";
import CustomCSSRenderer from "../../components/CustomCSSRenderer";
import FirebaseAuthGuard from "../../components/FirebaseAuthGuard";
import hasContent from "../../util/hasContent";
import useViewStatistics from "../../components/hooks/useViewStatistics";
import pageKind from "../../data/enums/pageKind";

export default function Page() {
  useViewStatistics();

  const router = useRouter();
  const { event, page } = router.query;
  const [pageData, setPageData] = useState(null);
  const [showContent, setShowContent] = useState(false);
  // const [pageActionPointsList, setPageActionPointsList] = useState(null)
  const { globalPageData, setGlobalPageData } = useContext(
    GlobalPageDataContext
  );

  const { me } = useContext(UserContext);
  const { hasUnreadMessages } = useContext(ChatContext);
  const pageKit = new PageKit();

  function fetchPageDetail() {
    if ((event, page)) {
      const pageSlug = `${event}/${page}`;
      const globalPageItem = globalPageData[pageSlug];
      if (globalPageItem) {
        setPageData(globalPageItem);
        setShowContent(true);
      } else {
        pageKit
          .getPageDetail(event, page)
          .then((res) => {
            setPageData(res);
            setGlobalPageData((prevState) => ({
              ...prevState,
              [pageSlug]: res,
            }));
            setShowContent(true);
          })
          .catch((errors) => {
            console.log(errors);
            setShowContent(true);
            // router.push("/[event]/register", `/${event}/register`)
          });
      }
    }
  }

  useEffect(() => {
    fetchPageDetail();
  }, [event, page]);

  useEffect(() => {
    if (me && pageData && pageData.isPreSpeedMeetingRoom) {
      let deleteUser;
      let unbindRedirectListener;
      navigator.mediaDevices.getUserMedia({
        video: true, audio: true
      }).then(stream => {
        deleteUser = setSpeedMeetingUserData();
        unbindRedirectListener = listenToSpeedMeetingRedirectURL();
        if (typeof deleteUser === "undefined" && typeof unbindRedirectListener === "undefined") {
          deleteUser = () => { }
          unbindRedirectListener = () => { }
        }
      }).catch((err) => {
        console.error(`you got an error: ${err}`);
      });

      return () => {
        unbindRedirectListener();
        deleteUser();
      };
    }
  }, [pageData]);

  function listenToSpeedMeetingRedirectURL() {
    return SpeedMeetingKit.listenToRedirectUrl({
      eventSlug: event,
      pageSlug: page,
      eventUserId: me?.id,
      onChange: (snapshot) => {
        const redirectURL = snapshot.val();

        if (redirectURL) {
          router.push("/[event]/[page]", redirectURL);
        }
      },
    });
  }

  function setSpeedMeetingUserData() {
    const data = SpeedMeetingKit.setUserData({
      eventSlug: event,
      pageSlug: page,
      eventUserId: me?.id,
    });
    if (data) return data
  }

  function renderContent() {
    if (pageData.kind === pageKind.MAIN_PAGE) {
      return (
        <>
          {(pageData.heading || hasContent(pageData.description)) && (
            <div className="view__info">
              {pageData.heading && <h2>{pageData.heading}</h2>}
              <HTMLRenderer html={pageData.description} />
            </div>
          )}

          <InteractiveVideoPlayer key={pageData.id} {...pageData} />
        </>
      );
    }
    if (pageData.kind === pageKind.VIDEO_PAGE) {
      return <VideoPlayer key={pageData.id} {...pageData} />;
    }
    if (pageData.kind === pageKind.MEETING_PAGE) {
      return (
        <MeetingPlayer
          key={pageData.id}
          {...pageData}
          isModerator={me?.isModerator}
        />
      );
    }
    if (pageData.kind === pageKind.PRIVATE_MEETING_PAGE) {
      return (
        <MeetingPlayer
          key={pageData.id}
          {...pageData}
          isModerator={me?.isModerator}
        />
      );
    }
  }

  return (
    <>
      <FirebaseAuthGuard>
        <LayoutPrivate>
          {showContent && (
            <>
              {pageData && (
                <>
                  <PageHead
                    pageTitle={pageData.title}
                    eventTitle={pageData.event.title}
                    hasUnreadMessages={hasUnreadMessages}
                    pageCustomCss={pageData.customCss}
                  />
                  <EventBrowserInitView
                    children={renderContent}
                    mainPage={pageData}
                    event={event}
                  />
                  <EventMobileInitView
                    children={renderContent}
                    mainPage={pageData}
                    event={event}
                  />
                </>
              )}

              {!pageData && <Custom404 text="This view is not available" />}
            </>
          )}
        </LayoutPrivate>
      </FirebaseAuthGuard>
    </>
  );
}

Page.getInitialProps = async ({ query: { event, page } }) => {
  return {
    event,
    page,
  };
};
